<template>
    <USlideover
        v-model:open="visible"
        side="right"
        :close="false"
        :ui="{ content: 'max-w-2xl', body: 'p-0 sm:p-0', header: 'p-0 sm:px-0 min-h-0 block' }"
    >
        <template #header>
            <div class="flex items-start gap-3 px-4 pt-4 sm:px-6">
                <div class="min-w-0 flex-1">
                    <div class="flex flex-wrap items-center gap-2 text-xs">
                        <span v-if="task" class="font-mono font-medium text-muted">{{ task.key }}</span>
                        <!-- The project only for whoever may open it (docs/API.md → GET /tasks/{id}). -->
                        <RouterLink
                            v-if="task && task.project.can_view && appStore.hasModule(Module.Naloge)"
                            :to="{ name: 'project', params: { id: task.project.id }, query: { task: task.id } }"
                            class="truncate text-dimmed hover:text-default hover:underline"
                        >
                            {{ task.project.name }}
                        </RouterLink>
                        <AppTag v-if="task" :item="getTaskStatusTag(task.status)" size="sm" />
                        <UBadge v-if="task?.overdue" color="error" variant="subtle" size="sm" icon="i-lucide-clock-alert" :label="$t('task.overdue')" />
                        <UBadge v-if="task?.blocked" color="warning" variant="subtle" size="sm" icon="i-lucide-lock" :label="$t('task.blocked')" />
                    </div>
                    <h2 class="mt-1 text-lg font-semibold text-highlighted break-words">
                        {{ task?.title ?? (loading ? $t('general.loading') : $t('task.task')) }}
                    </h2>
                </div>

                <UButton
                    color="neutral"
                    variant="ghost"
                    icon="i-lucide-x"
                    :aria-label="$t('general.close')"
                    class="-mr-2 shrink-0"
                    @click="taskDrawer.close()"
                />
            </div>

            <div v-if="task" class="mt-2 overflow-x-auto px-2 pb-px sm:px-4">
                <UTabs
                    v-model="tab"
                    :items="tabs"
                    :content="false"
                    variant="link"
                    size="sm"
                    class="w-max min-w-full"
                    :ui="{ list: 'border-b-0' }"
                />
            </div>
        </template>

        <template #body>
            <div v-if="loading && !task" class="flex justify-center py-16">
                <UIcon name="i-lucide-loader-circle" class="size-6 animate-spin text-muted" />
            </div>

            <div v-else-if="errorText" class="p-4 sm:p-6">
                <UAlert color="error" icon="i-lucide-circle-alert" :title="errorText" />
            </div>

            <template v-else-if="task">
                <TaskDetailsForm
                    v-if="tab === 'details'"
                    :key="`details-${task.id}-${detailsKey}`"
                    :task="task"
                    :members="members"
                    :project-tasks="projectTasks"
                    :can-delete="canDelete"
                    @saved="onSaved"
                    @deleted="onDeleted"
                />

                <TaskComments
                    v-else-if="tab === 'comments'"
                    :task-id="task.id"
                    @changed="onChildChanged('comments', $event)"
                />

                <TaskAttachments
                    v-else-if="tab === 'attachments'"
                    :task-id="task.id"
                    @changed="onChildChanged('attachments', $event)"
                />

                <div v-else class="p-4 sm:p-6">
                    <div v-if="activityLoading && activity.length === 0" class="flex justify-center py-8">
                        <UIcon name="i-lucide-loader-circle" class="size-5 animate-spin text-muted" />
                    </div>
                    <ActivityList v-else :activities="activity" compact />
                </div>
            </template>
        </template>
    </USlideover>
</template>

<script lang="ts" setup>
    import type { TabsItem } from '@nuxt/ui'
    import { computed, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { RouterLink } from 'vue-router'

    import ActivityList from '@/components/ActivityList.vue'
    import AppTag from '@/components/app/AppTag.vue'
    import TaskAttachments from '@/components/task/TaskAttachments.vue'
    import TaskComments from '@/components/task/TaskComments.vue'
    import TaskDetailsForm from '@/components/task/TaskDetailsForm.vue'
    import { Module } from '@/enums/module'
    import { parseKnownError } from '@/libs/error'
    import { getTaskStatusTag } from '@/libs/task'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import { useTaskDrawerStore } from '@/stores/taskDrawer'
    import type { IActivity } from '@/types/Activity'
    import type { IProject, IProjectMember } from '@/types/Project'
    import type { ITask, ITaskDetail, ITaskMin } from '@/types/Task'

    /*
     * THE TASK, IN A DRAWER — one for the whole console (App.vue), opened by `?task=<id>` on
     * any page (`stores/taskDrawer.ts`).
     *
     * It asks for the task first, because the task says which project it is in, and then for
     * the three things the form needs from that project: its members (the only people a task
     * may be given to, docs/API.md → Permissions), its tasks (the only possible predecessors)
     * and the history. An answer that arrives after the reader has already moved on to
     * another task is dropped.
     *
     * A task whose project the reader may not see (`project.can_view` false, docs/API.md →
     * GET /tasks/{id}) has no project link and no dependencies, and the project is not asked for.
     */
    type DrawerTab = 'details' | 'comments' | 'attachments' | 'activity'

    const { t }      = useI18n()
    const appStore   = useAppStore()
    const fetchStore = useFetchStore()
    const taskDrawer = useTaskDrawerStore()

    const task            = ref<ITaskDetail>()
    const project         = ref<IProject>()
    const projectTasks    = ref<ITask[]>([])
    const activity        = ref<IActivity[]>([])
    const loading         = ref(false)
    const activityLoading = ref(false)
    const errorText       = ref('')
    const tab             = ref<DrawerTab>('details')
    const detailsKey      = ref(0)

    const visible = computed({
        get: (): boolean => !!taskDrawer.taskId,
        set: (open: boolean) => { if (!open) taskDrawer.close() },
    })

    const members = computed((): IProjectMember[] => project.value?.members ?? [])

    const canDelete = computed((): boolean => !!task.value && (
        task.value.project.can_edit || task.value.created_by?.id === appStore.userData?.id
    ))

    const tabs = computed((): TabsItem[] => [
        { label: t('task.tab_details'), value: 'details', icon: 'i-lucide-file-text' },
        {
            label: t('task.tab_comments'),
            value: 'comments',
            icon:  'i-lucide-message-square',
            badge: task.value?.comment_count ? { label: String(task.value.comment_count), color: 'neutral', variant: 'subtle' } : undefined,
        },
        {
            label: t('task.tab_attachments'),
            value: 'attachments',
            icon:  'i-lucide-paperclip',
            badge: task.value?.attachment_count ? { label: String(task.value.attachment_count), color: 'neutral', variant: 'subtle' } : undefined,
        },
        { label: t('task.tab_activity'), value: 'activity', icon: 'i-lucide-history' },
    ])

    const loadActivity = async (id: string) => {
        activityLoading.value = true

        const { statusCode, data } = await fetchStore.fetch(`/tasks/${id}/activity`).get().json<IActivity[]>()

        if (taskDrawer.taskId === id && statusCode.value === 200) activity.value = data.value ?? []

        activityLoading.value = false
    }

    const loadProject = async (id: string, projectId: string) => {
        const [projectAnswer, tasksAnswer] = await Promise.all([
            fetchStore.fetch(`/projects/${projectId}`).get().json<IProject>(),
            fetchStore.fetch(`/projects/${projectId}/tasks`).get().json<ITask[]>(),
        ])

        if (taskDrawer.taskId !== id) return

        if (projectAnswer.statusCode.value === 200 && projectAnswer.data.value) project.value = projectAnswer.data.value
        if (tasksAnswer.statusCode.value === 200) projectTasks.value = tasksAnswer.data.value ?? []
    }

    /** Asks for the task; `quiet` keeps what is on screen while it does (after a save). */
    const load = async (id: string, quiet = false) => {
        if (!quiet) {
            loading.value   = true
            errorText.value = ''
        }

        const { statusCode, data } = await fetchStore.fetch(`/tasks/${id}`).get().json<ITaskDetail>()

        if (taskDrawer.taskId !== id) return

        loading.value = false

        if (statusCode.value !== 200 || !data.value) {
            task.value      = undefined
            errorText.value = statusCode.value === 403 || statusCode.value === 404
                ? t('task.not_available')
                : parseKnownError(data.value, t('task.load_failed'))

            return
        }

        const projectChanged = task.value?.project.id !== data.value.project.id

        task.value = data.value

        // Another project's members and tasks must not stay behind for one that is not asked for.
        if (projectChanged) {
            project.value      = undefined
            projectTasks.value = []
        }

        await Promise.all([
            // A project the reader may not see is not asked for — it would only answer 403.
            data.value.project.can_view && (projectChanged || !quiet) ? loadProject(id, data.value.project.id) : Promise.resolve(),
            loadActivity(id),
        ])
    }

    watch(() => taskDrawer.taskId, (id, previous) => {
        if (!id) return

        if (id !== previous) {
            tab.value      = 'details'
            task.value     = undefined
            activity.value = []
        }

        void load(id)
    }, { immediate: true })

    /*
     * A save answers with the task and with whatever the cascade pushed later; the pages under
     * the drawer are told (`notify`), and the drawer reads the task again for its neighbours
     * and history, which the answer does not carry.
     */
    const onSaved = async (saved: ITask, shifted: ITaskMin[]) => {
        taskDrawer.notify({ task: saved, shifted })

        await load(saved.id, true)

        // The form starts again from what the server now has, not from what was typed.
        detailsKey.value++
    }

    const onDeleted = (id: string) => {
        taskDrawer.notify({ removedId: id, shifted: [] })
        taskDrawer.close()
    }

    /** A comment or an attachment came or went: the tab's count and the history follow. */
    const onChildChanged = (kind: 'comments' | 'attachments', count: number) => {
        if (!task.value) return

        if (kind === 'comments') task.value.comment_count = count
        else task.value.attachment_count = count

        taskDrawer.notify({ task: task.value, shifted: [] })
        void loadActivity(task.value.id)
    }
</script>
